import React, { createContext } from 'react';

import invariant from 'tiny-invariant';

// When a user drags & drops a navigation item, ADS recommends that we flash the item visually, and if the move was
// triggered by keyboard, also set focus to the moved item. This element registry allows GSN to keep track of the
// actual HTML elements of the L1 items, so that we can flash and focus them after the drag & drop action is performed.
// This pattern is recommended by AtlasKit in the side nav drag & drop documentation:
// https://atlassian.design/components/navigation-system/side-nav-items/drag-and-drop#on-drop

const MORE_MENU_ID = Symbol('moreMenuId');
type IdType = string | typeof MORE_MENU_ID;

export function createElementRegistry() {
	const registry = new Map<IdType, HTMLElement>();

	function registerL1Item({ id, element }: { element: HTMLElement; id: IdType }) {
		registry.set(id, element);
		return function cleanup() {
			registry.delete(id);
		};
	}

	function getL1ItemElement(id: IdType): HTMLElement | undefined {
		return registry.get(id);
	}

	function registerMoreMenuItem(element: HTMLButtonElement) {
		return registerL1Item({ id: MORE_MENU_ID, element });
	}

	function getMoreMenuElement(): HTMLButtonElement | undefined {
		return getL1ItemElement(MORE_MENU_ID) as HTMLButtonElement | undefined;
	}

	return {
		registerL1Item,
		getL1ItemElement,
		registerMoreMenuItem,
		getMoreMenuElement,
	};
}

export type ElementRegistry = ReturnType<typeof createElementRegistry>;

const ElementRegistryContext = createContext<ElementRegistry | null>(null);

export function ElementRegistryProvider({
	children,
	registry,
}: {
	children: React.ReactNode;
	registry: ElementRegistry;
}): React.JSX.Element {
	return (
		<ElementRegistryContext.Provider value={registry}>{children}</ElementRegistryContext.Provider>
	);
}

export function useElementRegistry() {
	const elementRegistry = React.useContext(ElementRegistryContext);
	invariant(elementRegistry, 'useElementRegistry must be used within an ElementRegistryProvider');
	return elementRegistry;
}
